<template>
  <div id="news-article">
    <Breadcrumbs
      :crumbs="[
        { name: globals.info.school_name, link: `${pathPrefix}/` },
        { name: crumbName, link: `${pathPrefix}/news` },
        { name: replaceHTMLEntities(article.title), link: `${pathPrefix}/article/${article.id}` },
      ]"
    />
    <LoadingIndicator v-if="!article?.content" />
    <div
      v-else
      class="page-container"
    >
      <div class="page-container-item">
        <div
          v-if="article.cover_image"
          class="article-image"
        >
          <img
            :alt="article.image_description || article.title"
            :src="article.cover_image"
            onerror="style='display:none;'"
          />
        </div>
        <div class="article-info">
          <h2 class="title">
            {{ replaceHTMLEntities(article.title) }}
          </h2>
          <p class="author">
            {{ article.author }}
          </p>
          <p class="date">
            {{ formatArticleDate(article) }}
          </p>
        </div>
        <div class="article-content">
          <!-- eslint-disable-line vue/no-v-html -->
          <div v-html="article.content" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue';
import { useMainStore } from '../../../stores/mainStore';
import Breadcrumbs from '../../../components/global/Breadcrumbs.vue';
import LoadingIndicator from '~/components/global/LoadingIndicator.vue';
import { replaceHTMLEntities } from '../../../helpers/strings';
import { clientWorkWindowEvent } from '../../../helpers/event-dispatch-helpers';
import { getFileTypeFromUrl } from '../../../helpers/metadata';
import { useNuxtApp, useRoute, useAsyncData, createError, useHead } from '#imports';

defineOptions({
  name: 'NewsArticle',
});

const { $d, $axios, $t } = useNuxtApp();
const mainStore = useMainStore();
const globals = computed(() => mainStore.globals);
const domain = computed(() => mainStore.domain);
const pathPrefix = computed(() => mainStore.pathPrefix);
const customSections = computed(() => mainStore.customSections);

onMounted(() => {
  clientWorkWindowEvent('news-article');
});

const newsApi = mainStore.getNews($axios.defaults.baseURL);
const { url, params: apiParams = {} } = typeof newsApi === 'string' ? { url: newsApi, params: {} } : newsApi;
const articleId = useRoute().params.id;

const { data: articleData, error } = await useAsyncData(`article-${articleId}`, async () => {
  const { data } = await $axios.get(`${url}/${articleId}`, { params: apiParams, cache: { ignoreCache: false } });
  return data.article;
});

if (!articleData.value && error.value.statusCode) {
  throw createError({
    statusCode: error.value.statusCode || 500,
    statusMessage: error.value.message || 'Unexpected server error',
  });
}

const newsDefaultFallbackImageUrl = globals.value?.fallback_images?.find((item) => item.feature === 'news')?.url || '';

let metaCoverImage = '';

const fetchedArticleData = articleData.value;
if (fetchedArticleData.cover_image) {
  metaCoverImage = fetchedArticleData.cover_image;
} else if (mainStore.settings.newsFallbackImage?.url) {
  metaCoverImage = mainStore.settings.newsFallbackImage.url;
} else {
  metaCoverImage = newsDefaultFallbackImageUrl;
}

const metaCoverImageType = getFileTypeFromUrl(metaCoverImage);
const metaArticleUrl = `https://${domain.value}${pathPrefix.value}/article/${articleId}`;

useHead({
  title: fetchedArticleData.title,
  meta: [
    {
      hid: 'og:type',
      vmid: 'og:type',
      property: 'og:type',
      content: 'website',
    },
    {
      hid: 'og:url',
      vmid: 'og:url',
      property: 'og:url',
      content: metaArticleUrl,
    },
    {
      hid: 'og:title',
      vmid: 'og:title',
      property: 'og:title',
      content: fetchedArticleData.title,
    },
    {
      hid: 'og:description',
      vmid: 'og:description',
      property: 'og:description',
      content: fetchedArticleData.content_text,
      tagPriority: 'critical',
    },
    {
      hid: 'og:image',
      vmid: 'og:image',
      property: 'og:image',
      content: metaCoverImage,
      tagPriority: 'critical',
    },
    {
      hid: 'og:image:type',
      vmid: 'og:image:type',
      property: 'og:image:type',
      content: metaCoverImageType,
      tagPriority: 'critical',
    },
    { hid: 'twitter:site', name: 'twitter:site', content: metaArticleUrl },
    { hid: 'twitter:title', name: 'twitter:title', content: fetchedArticleData.title },
    { hid: 'twitter:description', name: 'twitter:description', content: fetchedArticleData.content_text },
    { hid: 'twitter:image', name: 'twitter:image', content: metaCoverImage, tagPriority: 'critical' },
  ],
});

const article = computed(() => articleData.value || {});

watch(error, (e) => {
  if (e) {
    throw createError({ statusCode: e.value.response.status });
  }
});

const crumbName = computed(() => {
  const customSection = customSections.value.find((s) => s.type === 'articles');
  return customSection ? customSection.name : $t('pages.news.sectionName');
});

function formatArticleDate(article) {
  if (!article.published_at) return '';
  return $d(article.published_at, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
</script>

<style scoped lang="scss">
#news-article {
  .page-container {
    max-width: 1024px;
  }
  .page-container-item {
    border: none;
    .article-image img {
      max-width: 100%;
      height: auto;
    }
    .article-info {
      margin: 20px 0;
      h2 {
        color: var(--primary-color);
      }
    }
    .title {
      font-size: 24px;
      font-weight: 400;
      text-transform: uppercase;
    }
    .author,
    .date {
      margin-top: 8px;
      color: #595959;
    }
    .article-content {
      overflow-x: auto;
      :deep(img) {
        max-width: 100%;
        height: auto;
        @media screen and (max-width: 1023px) {
          height: auto !important;
        }
      }
      & :deep(p) {
        margin-bottom: 19px;
        min-height: 20px;
        & a {
          color: var(--links-color);
        }
      }
      :deep(table) {
        width: 100%;
        height: fit-content;
        table-layout: fixed;
        overflow-y: scroll;
        border-collapse: collapse;
        border: 1px solid #000;
        word-wrap: break-word;
        tr {
          td,
          th {
            border: 1px solid #000;
            padding: 15px 20px;
            vertical-align: top;
          }
        }
      }
      :deep() {
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
          margin-bottom: 19px;
        }
      }
      :deep(ul p),
      :deep(ol p) {
        margin-bottom: 0px;
      }
    }
  }
}
</style>
